/**
 * Progress Bar Module
 * Manages the visual progress bar for audio playback
 */

import appState from '../../core/app-state.js';

/**
 * Update playback progress bar (non-interactive, visual only)
 */
export function updatePlaybackProgressBar(audioEl: HTMLAudioElement, forceComplete = false): void {
  const progressBar = document.getElementById('playbackProgressBar');
  const progressBarFill = document.getElementById('playbackProgressBarFill');
  
  if (!progressBar || !progressBarFill) return;
  
  // Handle edge cases: NaN, Infinity, or audio not ready
  if (forceComplete || audioEl.ended) {
    // Force 100% on ended
    progressBarFill.style.width = '100%';
    progressBar.setAttribute('aria-valuenow', '100');
    return;
  }
  
  const duration = audioEl.duration;
  const currentTime = audioEl.currentTime;
  
  // If duration is not valid, show 0%
  if (!duration || isNaN(duration) || !isFinite(duration)) {
    progressBarFill.style.width = '0%';
    progressBar.setAttribute('aria-valuenow', '0');
    return;
  }
  
  // Calculate percentage
  const percent = Math.min(100, Math.max(0, (currentTime / duration) * 100));
  const percentRounded = Math.round(percent);
  
  // Update visual width
  progressBarFill.style.width = `${percent}%`;
  
  // Update ARIA attribute (throttled to ~4-5 fps to avoid screen reader spam)
  const now = Date.now();
  if (now - appState.lastAriaUpdate > 200) { // 200ms = 5fps
    progressBar.setAttribute('aria-valuenow', String(percentRounded));
    appState.lastAriaUpdate = now;
  }
}

/**
 * Start RAF-driven progress animation
 */
export function startProgressAnimation(audioEl: HTMLAudioElement): void {
  // Stop any existing animation
  stopProgressAnimation();
  
  const animate = () => {
    // Check if document is hidden (tab in background)
    if (document.visibilityState === 'hidden') {
      // Pause animation but keep the RAF ID so we can resume
      appState.progressAnimationId = requestAnimationFrame(animate);
      return;
    }
    
    // Update progress bar
    updatePlaybackProgressBar(audioEl);
    
    // Continue animation if audio is still playing
    if (audioEl && !audioEl.paused && !audioEl.ended) {
      appState.progressAnimationId = requestAnimationFrame(animate);
    } else {
      appState.progressAnimationId = null;
    }
  };
  
  appState.progressAnimationId = requestAnimationFrame(animate);
}

/**
 * Stop RAF-driven progress animation
 */
export function stopProgressAnimation(): void {
  if (appState.progressAnimationId !== null) {
    cancelAnimationFrame(appState.progressAnimationId);
    appState.progressAnimationId = null;
  }
}
